/**
 * Traducao de referencia (ingles).
 *
 * Este objeto define o "shape" das traducoes: e a partir de `typeof en` que a
 * tipagem forte de `t()` e derivada (ver src/@types/i18next.d.ts). Toda chave
 * aqui presente passa a ser exigida (ou ao menos reconhecida) pelas demais
 * traducoes. Chaves ausentes em outros idiomas caem automaticamente para o
 * ingles via fallbackLng.
 *
 * Organizacao por dominio/feature, nao por lista solta de strings.
 */
var en = {
    common: {
        next: 'Next',
        back: 'Back',
        cancel: 'Cancel',
        confirm: 'Confirm',
        continue: 'Continue',
        tryAgain: 'Try again',
        loading: 'Loading...',
        secureEnvironment: 'You are in a secure environment',
    },
    termsConsent: {
        progress: 'STEP {{current}} OF {{total}}',
        title: 'Your privacy matters',
        subtitle: 'Before you continue, learn how your data will be used.',
    },
    initial: {
        noConfiguration: 'We could not find any configuration for the requested product',
        initialImageAlt: 'Initial image',
        tryAgain: 'Try again',
        version: 'Version {{version}}',
    },
    confirmation: {
        success: 'Onboarding completed successfully! Query token: {{token}}',
        restart: 'Restart Onboarding',
    },
    errors: {
        generic: 'An error occurred. Please try again.',
        required: 'This field is required.',
        fetchParams: 'Failed to fetch parameters',
        productNotFound: 'Product not found, please generate another one!',
        insufficientBalance: 'Could not start this onboarding, please contact the company',
        onboardingTokenFailed: 'Failed to obtain the onboarding token.',
        invalidOnboarding: 'Invalid onboarding, please generate another one!',
        invalidOnboardingRetry: 'Invalid onboarding. Please try again.',
        invalidToken: 'Invalid token',
        onboardingAlreadyFinished: 'Onboarding already finished, please generate another one if you want to redo it!',
        tokenCanceled: 'Token has already been canceled, please generate another one!',
        passkeyStepInvalid: 'Invalid Passkey step.',
        passkeyInvalidResponse: 'Invalid response while saving the passkey result.',
        pdfError: 'Error processing the PDF. Please try again.',
    },
    form: {
        scrollForMore: 'Scroll to see more fields',
        required: 'Required field',
        invalidCpf: 'Invalid CPF',
        invalidCnpj: 'Invalid CNPJ',
        invalidRg: 'Invalid RG',
        invalidZipcode: 'Invalid ZIP code',
        invalidAccount: 'Invalid account',
        invalidBankAgency: 'Invalid branch',
        invalidEmail: 'Invalid email',
        invalidPhone: 'Invalid phone',
        invalidPassword: 'Invalid password',
        passwordsDoNotMatch: 'Passwords do not match',
    },
    passkey: {
        title: 'Identity verification',
        descriptionLogin: 'Use your passkey to reuse your identity verification.',
        descriptionRegister: 'Register a passkey to protect and reuse your identity verification.',
        descriptionValidating: 'Please wait while we validate your passkey.',
        loginAction: 'Sign in with passkey',
        registerAction: 'Register passkey',
        fullNameLabel: 'Full name *',
        cpfLabel: 'CPF *',
        birthDateLabel: 'Date of birth *',
        fullNameRequired: 'Enter the full name.',
        cpfInvalid: 'Enter a valid CPF.',
        birthDateInvalid: 'Enter a valid date of birth.',
        saveFailed: 'Could not save the passkey result. Please try again.',
        dataMissing: 'The data required for the passkey is missing or invalid.',
        notSupported: 'Passkeys are not supported in this browser.',
        authSuccess: 'Passkey authentication completed successfully.',
        registerSuccess: 'Passkey registered successfully. Continue to complete your verification.',
        authFailed: 'Could not authenticate with the passkey. Verification will continue normally.',
        registerFailed: 'Could not register the passkey. Verification will continue normally.',
        continuesNormally: '{{message}} Verification will continue normally.',
    },
    flow: {
        // Interpolacao + pluralizacao: quando N steps sao puladas.
        stepAlreadyDone_one: 'This step has already been completed. Skipping {{count}} step...',
        stepAlreadyDone_other: 'This step has already been completed. Skipping {{count}} steps...',
    },
    camera: {
        failureTitle: 'Unable to open the camera',
        unavailable: 'Open this registration on an HTTPS page in Safari and allow camera access.',
        retryAccess: 'Unable to start the camera. Close other apps using the camera and try again.',
        decisionFailed: 'Unable to proceed to document submission. Check your connection and try again.',
        notFoundTitle: 'Camera not found',
        notFoundMessage: 'Please check whether your device has an active camera and that it is properly configured.',
        enableTitle: 'Enable your camera',
        enableMessage: 'Camera permissions are disabled for the browser! Please check the permission in your browser settings.',
        permissionDenied: 'Camera permission was not granted. Please grant camera permission to the page.',
        permissionDeniedShort: 'Camera permission denied. Please allow access to the camera.',
        notFoundOnDevice: 'No camera found on the device.',
        inUse: 'Camera is being used by another application. Close other apps that use the camera.',
        unsupportedConfig: 'Unsupported camera configuration. Trying with default settings.',
        accessError: 'Error accessing camera: {{message}}',
        invalidFormat: 'Invalid format.',
        lowQuality: 'Low image quality! Please choose another one!',
        switchCamera: 'Switch camera',
        loadError: 'An error occurred while loading your device camera. Please check the permissions or try another browser.',
        chooseDocumentBack: 'Choose the back of the document, the side without the photo!',
        chooseDocumentFront: 'Choose the front of the document, the side with the photo!',
        invalidCnpjDocument: 'Invalid document, choose a CNPJ card, articles of association or bylaws!',
        pdfMaxTwoPages: 'The PDF must contain at most two pages (front and back).',
        pdfMaxThirtyPages: 'The articles of association or bylaws must contain at most 30 pages.',
        lowImageQuality: 'Low image quality! Please choose another one!',
        takingImageError: 'An error occurred while taking the picture. Please try again.',
        fileTooLarge: 'File too large. Please upload a file smaller than 10MB.',
        lockFile: 'The submitted file is password-protected. Please submit a file without a password.',
        blurryImage: 'The image is too blurry. Hold your phone steady and try again.',
        darkImage: 'The image is too dark. Find a place with more light.',
        lowContrastImage: 'The image has low contrast. Move the camera closer and avoid glare.',
        lowResolutionImage: 'The image resolution is too low. Try again or upload a higher-resolution image.',
        acceptingLowQuality: 'Submitting despite low quality, since the photo was rejected multiple times.',
        noDocumentDetected: 'Position the document inside the frame.',
        holdSteady: 'Hold steady, it looks shaky.',
        misalignedDocument: 'Adjust the document inside the frame.',
        moveCloser: 'Move the document closer to the camera.',
        moveFarther: 'Move the document a bit farther away.',
        moveLeft: 'Move the document to the left.',
        moveRight: 'Move the document to the right.',
        moveUp: 'Move the document up.',
        moveDown: 'Move the document down.',
        wellFramedDocument: 'Document well positioned!',
    },
    liveness: {
        title: 'Photo for Verification',
        intro: 'We need to take a photo of you. Before you start:',
        tipLighting: 'Make sure you are in a <strong>well-lit</strong> environment.',
        tipAccessories: '<strong>Remove glasses, caps or any accessory that may hide your face.</strong>',
        tipCentered: 'Keep your face centered on the screen.',
        outro: 'Please pay attention to the instructions provided during the process.',
        startError: 'Could not start the liveness check. Please try again.',
        sessionError: 'Error getting session_id',
        frameFace: 'Frame your face in the drawn mask',
        startChallenge: 'Start challenge',
        startLiveness: 'Start Liveness',
    },
    onboardingHelp: {
        open: "Open help",
        close: "Close help",
        title: "How can we help?",
        done: "Got it",
        howTitle: "How it works",
        howText: "Follow the steps shown on screen. The information requested depends on the verification configured by the company.",
        documentTitle: "Upload your document",
        documentText: "Use good lighting, avoid glare and keep every edge visible. Check that the information is readable before confirming. If the File option is available, you can upload a file from your device.",
        livenessTitle: "Liveness check",
        livenessText: "Light your face, remove accessories that cover it and keep it inside the oval. Follow the gesture requested on screen and wait for verification.",
        cameraTitle: "Camera issues",
        cameraText: "Allow camera access when your browser asks. If access was blocked, open this site’s permissions in your browser and enable the camera. Close other apps using the camera and try again.",
        privacyTitle: "Privacy and data use",
        privacyText: "Read the privacy policy and terms presented in this flow to understand the purpose of data use and the verification conditions.",
        supportTitle: "Need more help?",
        supportText: "If the issue continues, contact the company that sent the verification link through its stated support channels.",
    },
    verificationIntro: {
        eyebrow: "YOUR IDENTITY, WITH CARE",
        title: "Shall we start?",
        description: "Verifying your identity can be simple. We will guide you through each step.",
        data: "Your details",
        dataDetail: "A little about you",
        document: "Identification document",
        documentDetail: "A photo, made simple",
        selfie: "A selfie",
        selfieDetail: "To confirm it is really you",
        minute: "1 min",
        seconds: "30 sec",
        start: "Start verification",
    },
    livenessLocal: {
        developerCredit: "Developed by ReactIt Solutions",
        verification: "FACIAL VERIFICATION",
        shortFrontal: "Look",
        shortLeft: "Left",
        shortRight: "Right",
        shortBlink: "Blink",
        shortSmile: "Smile",
        hintFrontal: "Keep your face centered and look at the camera.",
        hintLeft: "Turn your face to the left, keeping it inside the oval.",
        hintRight: "Turn your face to the right, keeping it inside the oval.",
        hintBlink: "Keep your head still and blink naturally.",
        hintSmile: "Look at the camera and smile naturally.",
        visualPreview: "VISUAL PREVIEW",
        cameraLive: "LIVE CAMERA",
        mockCameraNote: "Visual preview · no biometric validation",
        prepareSubtitle: "Get ready to confirm it is really you.",
        prepareLightingTitle: "Find good lighting",
        prepareLightingText: "Light your face without shadows or backlighting.",
        prepareFaceTitle: "Keep your face clear",
        prepareFaceText: "Remove glasses, hats and other accessories.",
        prepareCameraTitle: "Look at the camera",
        prepareCameraText: "Keep your entire face in the center of the screen.",
        prepareFollowTitle: "Follow the instructions",
        prepareNote: "The camera will turn on when you start. Follow the instructions to complete your liveness check.",
        prepareMockNote: "In simulation mode, capture is a demonstration and does not perform a liveness check.",
        prepareStart: "Start",
        starting: 'Preparing the challenge...',
        checking: 'Checking...',
        startFailed: 'Could not start the challenge. Please try again.',
        saveFailed: 'Could not save the result. Please try again.',
        cameraError: 'We could not capture the camera image.',
        cameraStarting: 'Preparing the camera...',
        cameraNotReady: 'The camera is not ready yet. Please wait a moment.',
        challengeNotIssued: 'The liveness service did not return a challenge. Please contact support.',
        introTitle: 'Liveness check',
        introSubtitle: 'We need to confirm it is really you',
        tipFollow: 'Follow the instructions that appear on the screen.',
        startButton: 'Start',
        successTitle: 'Liveness confirmed!',
        successMessage: 'All set. Saving your result...',
        failureTitle: 'We could not confirm your liveness',
        capturingIn: 'Capturing in {{seconds}}...',
        notApproved: 'We could not confirm liveness. Let us try again.',
        retry: 'Let us repeat the challenge.',
        gestureOk: 'Gesture recognized.',
        gestureRetry: 'We did not recognize the gesture. Please try again.',
        gestureCount: 'Gesture {{current}} of {{total}}',
        gestureFrontal: 'Look at the camera',
        gestureTurnLeft: 'Turn your face to the left',
        gestureTurnRight: 'Turn your face to the right',
        gestureBlink: 'Blink your eyes',
        gestureSmile: 'Smile',
    },
    livenessAws: {
        stepName: 'Liveness Check',
        takeSelfieTitle: 'Take a selfie',
        verifyIdentity: 'We need to verify your identity',
        tipLighting: 'Make sure you are in a well-<strong>lit</strong> environment.',
        tipAccessories: '<strong>Remove</strong> glasses, caps or any accessory that may hide your face.',
        tipCentered: 'Keep your face centered on the screen.',
        starting: 'Starting...',
        takeSelfieButton: 'Take Selfie',
        analyzing: 'We are analyzing your information',
        couldNotComplete: 'Could not complete the liveness check. Please try again.',
        lowQualitySelfie: 'Selfie below quality standards. Redo it in a well-lit environment, with your face centered and no accessories.',
        notValidated: 'Liveness check not validated. Please try again.',
        couldNotValidate: 'Could not validate the liveness check. Please try again.',
    },
    livenessDetector: {
        photosensitivityWarningHeadingText: 'Photosensitivity warning',
        photosensitivityWarningBodyText: 'This check flashes different colors. Use caution if you are photosensitive.',
        photosensitivityWarningInfoText: 'Some people may experience epileptic seizures when exposed to colored lights. Use caution if you, or anyone in your family, have an epileptic condition.',
        photosensitivityWarningLabelText: 'More information about photosensitivity',
        goodFitCaptionText: 'Good fit',
        goodFitAltText: "Illustration of a person's face, perfectly fitting inside of an oval.",
        tooFarCaptionText: 'Too far',
        tooFarAltText: "Illustration of a person's face inside of an oval; there is a gap between the perimeter of the face and the boundaries of the oval.",
        hintCenterFaceText: 'Center your face',
        hintCenterFaceInstructionText: 'Instruction: Before starting the check, make sure your camera is at the center top of your screen and center your face to the camera. When the check starts an oval will show up in the center. You will be prompted to move forward into the oval and then prompted to hold still. After holding still for a few seconds, you should hear check complete.',
        hintFaceOffCenterText: 'Face is not in the oval, center your face to the camera.',
        hintMoveFaceFrontOfCameraText: 'Move face in front of camera',
        hintTooManyFacesText: 'Ensure only one face is in front of camera',
        hintFaceDetectedText: 'Face detected',
        hintCanNotIdentifyText: 'Move face in front of camera',
        hintTooCloseText: 'Move back',
        hintTooFarText: 'Move closer',
        hintConnectingText: 'Connecting...',
        hintVerifyingText: 'Verifying...',
        hintCheckCompleteText: 'Check complete',
        hintIlluminationTooBrightText: 'Move to dimmer area',
        hintIlluminationTooDarkText: 'Move to brighter area',
        hintIlluminationNormalText: 'Lighting conditions normal',
        hintHoldFaceForFreshnessText: 'Hold still',
        hintMatchIndicatorText: '50% completed. Keep moving closer.',
        startScreenBeginCheckText: 'Start video check',
        cameraMinSpecificationsHeadingText: 'Camera does not meet minimum specifications',
        cameraMinSpecificationsMessageText: 'Camera must support at least 320*240 resolution and 15 frames per second.',
        cameraNotFoundHeadingText: 'Camera is not accessible.',
        cameraNotFoundMessageText: 'Check that a camera is connected and there is not another application using the camera. You may have to go into settings to grant camera permissions and close out all instances of your browser and retry.',
        a11yVideoLabelText: 'Webcam for liveness check',
        cancelLivenessCheckText: 'Cancel Liveness check',
        retryCameraPermissionsText: 'Retry',
        recordingIndicatorText: 'Rec',
        waitingCameraPermissionText: 'Waiting for you to allow camera permission.',
        errorLabelText: 'Error',
        connectionTimeoutHeaderText: 'Connection time out',
        connectionTimeoutMessageText: 'Connection has timed out.',
        timeoutHeaderText: 'Time out',
        timeoutMessageText: "Face didn't fit inside oval in time limit. Try again and completely fill the oval with face in it.",
        faceDistanceHeaderText: 'Forward movement detected',
        faceDistanceMessageText: 'Avoid moving closer when connecting.',
        multipleFacesHeaderText: 'Multiple faces detected',
        multipleFacesMessageText: 'Ensure only one face is present in front of the camera when connecting.',
        clientHeaderText: 'Client error',
        clientMessageText: 'Check failed due to client issue',
        serverHeaderText: 'Server issue',
        serverMessageText: 'Cannot complete check due to server issue',
        landscapeHeaderText: 'Landscape orientation not supported',
        landscapeMessageText: 'Rotate your device to portrait (vertical) orientation.',
        portraitMessageText: "Ensure your device remains in portrait (vertical) orientation for the check's duration.",
        tryAgainText: 'Try again',
    },
    decision: {
        title: 'Document Submission',
        readInstructions: 'Read the instructions before proceeding',
        chooseOption: 'Choose one of the options below',
        preSubmitGeneralTitle: 'Your document',
        preSubmitGeneralSubtitle: 'Read the guidance before proceeding.',
        preSubmitChecklistHeading: 'Before sending, check:',
        preSubmitChecklistHeadingPrefix: 'Before sending, ',
        preSubmitChecklistHeadingEmphasis: 'check:',
        preSubmitTipLightingTitle: 'Good lighting',
        preSubmitTipLightingDescription: 'Bright environment, with no shadows over the document.',
        preSubmitTipCompleteDocumentTitle: 'Complete document',
        preSubmitTipCompleteDocumentDescription: 'Everything visible, with no edges cut off.',
        preSubmitTipCorrectFileTitle: 'Correct file',
        preSubmitTipCorrectFileDescription: 'Photo or PDF up to 20 MB, clear when enlarged.',
        preSubmitGeneralTip: 'Tip: place the document on a dark, smooth table.',
        preSubmitSingleTitle: 'Single document',
        preSubmitSingleSubtitle: 'Front and back in the same photo.',
        preSubmitSingleHeading: 'Send the open document in a single photo.',
        preSubmitSingleStep: '01 ONE PHOTO',
        preSubmitSingleCaption: 'Front + back together',
        preSubmitSingleCheckOpen: 'Open the document to show both sides',
        preSubmitSingleCheckComplete: 'Entire document, without cutting off the edges',
        preSubmitSingleCheckLegible: 'Text and numbers clear when enlarged',
        preSubmitSingleCheckNoGlare: 'No glare, shadows, or fingers in front',
        preSubmitSingleTip: 'Use a dark, smooth background: contrast makes the text clearer.',
        preSubmitTwoSidedTitle: 'Front and back',
        preSubmitTwoSidedSubtitle: 'The submission happens in two steps.',
        preSubmitTwoSidedHeading: 'Send both sides, one at a time.',
        preSubmitTwoSidedHeadingPrefix: 'Send both sides, ',
        preSubmitTwoSidedHeadingEmphasis: 'one at a time.',
        preSubmitTwoSidedFront: 'Front',
        preSubmitTwoSidedFrontDescription: 'Photo, number, and signature clearly legible',
        preSubmitTwoSidedBack: 'Back',
        preSubmitTwoSidedBackDescription: 'Complete barcode, with no cuts',
        preSubmitTwoSidedCheckComplete: 'Entire document, without cutting off the edges',
        preSubmitTwoSidedCheckLegible: 'Text and numbers clear when enlarged',
        preSubmitTwoSidedCheckNoGlare: 'No glare, shadows, or fingers in front',
        preSubmitTwoSidedTip: 'Use a dark, smooth background: contrast makes the text clearer.',
    },
    image: {
        dataReviewFields: 'Your information',
        dataReviewFieldsHint: 'Review and edit the permitted fields.',
        dataReviewSelfie: 'Selfie',
        dataReviewTitle: 'Confirm your information',
        dataReviewSubtitle: 'We filled in the information found in your document. Review, correct or complete the fields before continuing.',
        dataReviewConfirm: 'Confirm information and continue',
        dataReviewOpenDocument: 'Open document',
        reviewBackendError: "Reading could not be completed. Try checking again; a submitted document will not be sent again.",
        reviewBackendRetry: "Check reading again",
        reviewBackendReading: "Sending and reading your document. Please wait?",
        reviewRetake: "Retake document photos",
        reviewOcrReference: "Extracted document information. Fields not found remain empty.",
        reviewLegibility_unavailable: "Document analysis was not approved. Check the available information.",
        reviewLegibility_unclear: "Reading inconclusive. Check the photo or retake it with better lighting and focus.",
        reviewLegibility_legible: "View document details",
        reviewSubtitle: "Review the information extracted from your document.",
        reviewNameMissing: "Name not identified",
        reviewPersonalData: "Full name",
        reviewDocumentDetails: "Document attached · View extracted information",
        reviewLiveness: "Liveness",
        reviewLivenessDone: "Liveness completed",
        reviewLivenessPending: "Liveness not completed yet",
        reviewDone: "Done",
        reviewLocalNotice: "The document has already been sent. This information was returned by the backend document analysis.",
        reviewTitle: "Does everything look right?",
        reviewMotherName: "Parentage · Mother's name",
        reviewFatherName: "Parentage · Father's name",
        reviewBirthplace: "Place of birth (city and state)",
        reviewParentageHint: "Parentage found on the document:",
        reviewName: "Full name",
        reviewBirthDate: "Date of birth",
        reviewReading: "Reading your document...",
        reviewOcrFailed: "The document could not be read automatically. Check your document and fill in the fields you can identify.",
        reviewOcrReady: "Check the recognized data and correct any inaccurate information.",
        reviewNotFound: "Not identified",
        reviewDocuments: "{{count}} document image(s) or file(s) attached",
        reviewNoticeTitle: "Check before sending",
        reviewNotice: "Automatic reading may contain errors. Your document will only be sent after you confirm below.",
        reviewSubmit: "Continue",
        previewTitle: "Review your document",
        previewSubtitle: "Check that all fields are legible before confirming.",
        previewImageAlt: "Attached document for review",
        previewAttached: "Document attached",
        previewWarningTitle: "Before confirming, check:",
        previewWarning: "Confirming sends your document for analysis. Wait for the reading before continuing.",
        previewConfirm: "Confirm document",
        reviewWarning: 'Make sure the image to be sent is clear and all fields are legible before confirming.',
        reviewWarningLabel: 'Attention:',
        introTitleTwoSided: 'Front and back submission',
        introTitleSingle: 'Document submission',
        introSubtitleTwoSided: 'The submission will be done in two steps.',
        introSubtitleSingle: 'You can upload a single file or take a photo.',
        introTextTwoSided: 'In this mode, the system will first request the front and then the back of the document. Make sure each side is legible, complete and well positioned in the image.',
        introTextSingle: 'In this mode, the document can be presented in a single file or a single image, as long as all the required information is legible and complete for analysis.',
        searchDigitalDocument: 'Search Digital Document',
        front: 'Front',
        back: 'Back',
        document: 'Document',
        backgroundTip: 'Prefer a dark, plain background for better document readability.',
        captureSingleTitle: 'Document single submission',
        processingDocument: 'Processing document...',
        preparingNextSide: 'Preparing the camera for the back...',
        captureHeader: 'DOCUMENT CAPTURE',
        captureInstruction: 'Position the document inside the marker',
        captureHint: 'Hold steady and avoid glare',
        captureFile: 'File',
        captureSwitch: 'Switch',
        captureSessionNote: 'Demo · photo only in this session',
    },
    selfie: {
        takePhoto: 'Take photo',
        retakePhoto: 'Take another photo',
        sendPhoto: 'Send photo',
        searchDigitalDocument: 'Search digital document',
    },
    success: {
        title: "Journey completed",
        description: "You have completed every step of this journey.",
        footer: "Secure environment • Your data is protected",
        step: "STEP {{current}} OF {{total}}",
        closingPage: 'Trying to close the page',
    },
    qrcode: {
        nextStep: 'Next step:',
        useCameraInfo: 'We will use your device camera to confirm it is you',
        continueOnThisDevice: 'Do you want to continue the registration on this device?',
        yesContinueHere: 'Yes, continue here',
        noUseAnotherDevice: 'No, use another device',
        scanInstruction: 'Use the QR Code below to continue on another device',
    },
    result: {
        title: 'Results',
        livenessAlt: 'Liveness check',
        loadFailed: 'We could not load the results.',
        updating: 'The results are still being updated.',
        updateFailed: 'We could not update right now. We will try again.',
        empty: 'No results available.',
        servicesLabel: 'Query results',
        statusApproved: 'Approved',
        statusNew: 'Waiting for processing',
        statusRejected: 'Rejected',
        statusProcessing: 'Processing',
        statusPending: 'Pending',
        statusPartial: 'Partial',
        statusError: 'Could not complete',
        statusUnavailable: 'Status unavailable',
    },
    serviceFields: {
        state: 'State',
        sex: 'Sex',
        mother_name: "Mother's name",
        rg_issuer: 'RG issuing authority',
        father_name: "Father's name",
        cnh_expiration: 'Driver license expiration',
        cnh_category: 'Driver license category',
        cnh_number: 'Driver license number',
        rg_number: 'RG number',
        name: 'Full name',
        cnh_paid_activity: 'Driver license paid activity',
        cnh_renach: 'Driver license RENACH',
        cnh_doc_emission_place: 'Driver license issuing place',
        classification: 'Classification',
        doc_type: 'Document type',
        file_type: 'File type',
        nationality: 'Nationality',
        valid_date: 'Expiration date',
        dt_rg_issue: 'RG issue date',
        cnh_last_emission_date: 'Driver license last issue date',
        cnh_first_qualification_date: 'First qualification date',
        birth_date: 'Date of birth',
        issue_date: 'Issue date',
        cpf: 'CPF',
        registration_number: 'Registration number',
        doc_emission_place: 'Document issuing place',
        issue_country: 'Issuing country',
        passaport_type: 'Passport type',
        passaport_number: 'Passport number',
        authority: 'Authority',
        naturality: 'Place of birth',
        is_organ_donor: 'Is an organ donor',
        via: 'Document copy',
        observations: 'Observations',
        rne_valid_date: 'RNE expiration date',
        rnm_number: 'RNM number',
        rne_number: 'RNE number',
        residence_period: 'Residence period',
        issue_body: 'Issuing authority',
        entry_date: 'Entry date',
        result_liveness_3d: 'Liveness 3D verification result',
    },
    serviceNames: {
        service_ocr: 'Extracting information from the identification document',
        service_liveness_3d_aws: 'Proof of Life',
        service_face_match: 'Face Match',
    },
    serviceMessages: {
        service_ocr__approved: 'OCR performed successfully',
        service_ocr__new: 'Document not found!',
        service_ocr__canceled: 'Document not found!',
        service_ocr__in_process: 'In progress',
        service_liveness_3d_aws__approved: 'Liveness successfully validated',
        service_liveness_3d_aws__refused: 'Unable to validate Liveness 3D',
        service_liveness_3d_aws__in_process: 'In progress',
        service_face_match__approved: 'Similarity Score: {{score}}',
        service_face_match__approved_fallback: 'Face matches',
        service_face_match__new: 'Identification document not found in the database',
        service_face_match__refused: 'Different faces',
        service_face_match__error: 'Failed to compare face with document',
    },
};
export default en;
